import { Link } from "react-router-dom"
import { useHead, useSeoMeta } from "@unhead/react"
import {
  calendlyUrl,
  faqDoula,
  images,
  prestations,
  serviceArea,
  travelFees,
} from "@/data/site"
import { jsonLdGraphScript, pageCanonical } from "@/lib/seo"
import {ArrowRight, CheckCircle2, ChevronDown, Heart, Sparkles, MapPin, ShieldCheck} from 'lucide-react'
import { useState } from "react"

function DoulaService() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const doulaOffer = prestations.find((p) => p.id === "doula")

  useSeoMeta({
    title: "Doula Plouay : préparation naissance Lorient",
    description:
      "Doula à Plouay et Lorient : séances à domicile ou en visio, pré-conception, grossesse et post-partum. Intervention dans un rayon de 35 km (Morbihan).",
    ogTitle: "Doula Plouay : préparation naissance Lorient",
    ogDescription:
      "Doula à Plouay et Lorient : séances à domicile ou en visio, pré-conception, grossesse et post-partum. Intervention dans un rayon de 35 km (Morbihan).",
    ogImage: doulaOffer?.image || images.yoga,
    twitterCard: "summary_large_image",
  })

  const doulaPageUrl = pageCanonical("/accompagnement-doula")

  useHead({
    script: [
      jsonLdGraphScript(
        [
          {
            name: doulaOffer?.title || "Accompagnement Doula",
            description: doulaOffer?.summary,
            url: `${doulaPageUrl}#${doulaOffer?.id || "doula"}`,
            image: doulaOffer?.image || images.yoga,
            serviceType: "Doula / accompagnante périnatale",
            offers: doulaOffer?.pricing || [],
          },
        ],
        "jsonld-offers-doula",
      ),
    ],
  })

  return (
    <main className="text-[#2d221b]">
      {/* Hero Section */}
      <section className="border-b border-[#dfd0c2] bg-gradient-to-b from-[#efe1d4] via-[#f7efe6] to-[#fbf7f2] px-5 py-14 md:py-20">
        <div className="mx-auto max-w-5xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#d6c4b6] bg-white/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.24em] text-[#b85c38] shadow-sm">
            <Sparkles size={14} />
            <span>Soutien continu & bienveillant</span>
          </div>
          <h1 className="mt-4 font-display text-4xl font-medium leading-tight text-[#2d221b] md:text-5xl lg:text-6xl">
            Doula à Inguiniel : accompagnement pré-conception, grossesse et post-partum
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#5e4b3e] md:text-lg">
            Un espace confidentiel et enveloppant pour déposer vos émotions, vos doutes et vos joies. Un soutien
            personnalisé à chaque étape de votre parentalité, du désir d'enfant aux premiers mois avec bébé, dans mon espace à Inguiniel, à votre domicile ou à distance en visio.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <a
              href="#doula"
              className="inline-flex items-center gap-2 rounded-full bg-[#b85c38] px-6 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-white shadow-warm-sm transition hover:bg-[#9d4c2e]"
            >
              <span>Voir les formules et tarifs</span>
              <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* Main Presentation */}
      <section className="px-5 py-12 md:py-16">
        <div className="mx-auto max-w-5xl">
          <div id={doulaOffer?.id} className="grid gap-10 rounded-3xl border border-[#dfd0c2] bg-[#fbf7f2] p-7 shadow-warm-xs md:grid-cols-2 md:p-10">
            <div>
              <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl border border-[#e2d5c8] bg-white p-2 shadow-sm">
                <img
                  src={doulaOffer?.image || images.yoga}
                  alt={doulaOffer?.title || "Accompagnement Doula"}
                  loading="lazy"
                  className="h-full w-full rounded-xl object-cover"
                />
              </div>
              <p className="mt-1.5 px-1 text-center text-[10px] italic text-[#8c6d58]">Photo non contractuelle</p>

              <div className="mt-8 rounded-2xl border border-[#ebdcd0] bg-[#efe1d4]/60 p-6 shadow-warm-xs">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#b85c38]">
                  <Sparkles size={16} />
                  <span>Mes engagements</span>
                </div>
                <ul className="mt-4 space-y-3 text-xs leading-relaxed text-[#5e4b3e]">
                  {doulaOffer?.details?.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[#b85c38]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="flex flex-col justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#b85c38]">
                  {doulaOffer?.kicker || "Grossesse · Naissance · Post-partum"}
                </p>
                <h2 className="mt-1 font-display text-2xl font-medium text-[#2d221b] md:text-3xl">
                  {doulaOffer?.title}
                </h2>
                <p className="mt-4 text-xs leading-relaxed text-[#685548] md:text-sm">
                  {doulaOffer?.summary}
                </p>

                <div className="mt-6 border-t border-[#ebdcd0] pt-6">
                  <h3 className="font-display text-xl font-medium text-[#2d221b]">
                    Séances à l'unité & Formules
                  </h3>
                  <div className="mt-4 divide-y divide-[#ede0d2] rounded-2xl border border-[#ebdcd0] bg-white p-5 text-sm shadow-warm-xs">
                    {doulaOffer?.pricing?.map((priceItem, pIdx) => (
                      <div
                        key={pIdx}
                        className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0"
                      >
                        <span className="text-xs font-medium text-[#4a3a30]">{priceItem.label}</span>
                        <span className="shrink-0 rounded-lg bg-[#efe1d4] px-3 py-1 font-display text-base font-bold text-[#8a4225]">
                          {priceItem.price}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href={calendlyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#b85c38] to-[#9d4c2e] px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.16em] text-white shadow-warm-md transition-all hover:scale-[1.02] hover:shadow-warm-lg"
                >
                  <span>Réserver une séance</span>
                  <ArrowRight size={14} />
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-[#cbbab0] bg-white/80 px-6 py-3.5 text-xs font-medium uppercase tracking-[0.16em] text-[#5e4b3e] shadow-sm transition hover:border-[#b85c38] hover:text-[#2d221b]"
                >
                  Poser une question
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Zone d'intervention */}
      <section className="px-5 py-6">
        <div className="mx-auto max-w-5xl rounded-3xl border border-[#dfd0c2] bg-gradient-to-r from-[#efe1d4] to-[#f7efe6] p-7 md:p-9 shadow-warm-xs">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#b85c38]">
                <MapPin size={14} />
                <span>Zone d'intervention</span>
              </div>
              <h3 className="mt-1 font-display text-2xl font-medium text-[#2d221b]">
                Où se déroulent les accompagnements ?
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-[#5e4b3e]">{serviceArea}</p>
              <p className="mt-1 text-xs text-[#8c6d58]">{travelFees}</p>
            </div>
            <Link
              to="/contact"
              className="inline-flex shrink-0 items-center justify-center rounded-full bg-[#6f4b3e] px-6 py-3 text-xs uppercase tracking-[0.16em] text-white transition hover:bg-[#5a3c32]"
            >
              Me contacter
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Doula */}
      <section className="px-5 py-14 md:py-20">
        <div className="mx-auto max-w-4xl">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#b85c38]">
              Questions fréquentes
            </p>
            <h2 className="mt-2 font-display text-3xl font-medium text-[#2d221b] md:text-4xl">
              Tout savoir sur l'accompagnement doula
            </h2>
          </div>

          <div className="mt-10 space-y-4">
            {faqDoula.map((faq, index) => {
              const isOpen = openFaq === index
              return (
                <div
                  key={index}
                  className="overflow-hidden rounded-2xl border border-[#dfd0c2] bg-[#fbf7f2] shadow-warm-xs transition-all hover:shadow-warm-sm"
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : index)}
                    className="flex w-full items-center justify-between gap-4 p-5 text-left font-display text-lg font-medium text-[#2d221b] hover:text-[#b85c38] focus:outline-none"
                    aria-expanded={isOpen}
                  >
                    <span>{faq.question}</span>
                    <ChevronDown
                      size={18}
                      className={`shrink-0 text-[#8c6d58] transition-transform duration-200 ${
                        isOpen ? "rotate-180 text-[#b85c38]" : ""
                      }`}
                    />
                  </button>
                  {isOpen ? (
                    <div className="border-t border-[#ede0d2] px-5 pb-5 pt-3">
                      <p className="text-xs leading-relaxed text-[#685548]">{faq.answer}</p>
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
        </div>
      </section>
    </main>
  )
}

export default DoulaService